import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  ENTITY_REGISTRATION_SOURCE,
  ENTITY_STATUS,
  ENTITY_TYPE,
  PERMISSIONS,
} from "../../constants";
import {
  ENTITY_DOC_STATUS_COLOR,
  ENTITY_DOC_STATUS_LABELS,
  ENTITY_REGISTRATION_SOURCE_LABELS,
  ENTITY_STATUS_COLOR,
  ENTITY_STATUS_LABELS,
  ENTITY_TYPE_LABELS,
} from "./entityLabels";
import { formatNit } from "./entityLogic";
import EntityForm from "./EntityForm";
import { fetchEntityPage } from "../../services/backend/EntityService";
import {
  appendEntityList,
  setEntityFilters,
  setEntityList,
  setEntityLoading,
  upsertEntity,
} from "../../redux/slices/entitySlice";

const PAGE_SIZE = 25;

// Un fallo de la consulta no puede verse igual que "no hay resultados": cada
// código de error de Firestore se traduce a algo accionable.
const LOAD_ERROR_MESSAGE = {
  "failed-precondition":
    "La consulta necesita un índice de Firestore que aún se está construyendo. Espera unos minutos y vuelve a recargar.",
  "permission-denied":
    "No tienes permiso para consultar estos datos. Revisa las reglas de Firestore del proyecto.",
};

export default function EntityList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);
  const { list, loading, filters } = useSelector((state) => state.entity);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const perms = user?.permissions || [];
  const roles = user?.roles || [];
  // ADMIN pasa por rol: los permisos son una foto por usuario, así que entity.*
  // no existe en las cuentas creadas antes de este módulo. Es el mismo criterio
  // que aplica handleEntity en el servidor.
  const isAdmin = roles.includes("ADMIN");
  const allowed = perms.includes(PERMISSIONS.ENTITY.LIST) || isAdmin;
  const canCreate = perms.includes(PERMISSIONS.ENTITY.CREATE) || isAdmin;

  const cursorRef = useRef(null);
  const inFlightRef = useRef(false);
  const generationRef = useRef(0);
  const sentinelRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadPage = useCallback(
    async (reset) => {
      if (!reset && inFlightRef.current) return;
      if (reset) {
        generationRef.current += 1;
        cursorRef.current = null;
        setHasMore(true);
        dispatch(setEntityLoading(true));
      } else {
        setLoadingMore(true);
      }
      const myGen = generationRef.current;
      inFlightRef.current = true;
      try {
        const { items, lastDoc, hasMore: more, error } = await fetchEntityPage({
          entityType: filters.entityType,
          status: filters.status,
          cursor: cursorRef.current,
          pageSize: PAGE_SIZE,
        });
        if (myGen !== generationRef.current) return;
        setLoadError(error || null);
        cursorRef.current = lastDoc;
        setHasMore(more);
        if (reset) dispatch(setEntityList(items));
        else dispatch(appendEntityList(items));
      } finally {
        if (myGen === generationRef.current) {
          inFlightRef.current = false;
          setLoadingMore(false);
          if (reset) dispatch(setEntityLoading(false));
        }
      }
    },
    [dispatch, filters.entityType, filters.status]
  );

  useEffect(() => {
    if (!allowed) {
      navigate("/home");
      return;
    }
    loadPage(true);
  }, [allowed, navigate, loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || !hasMore || inFlightRef.current) return;
        loadPage(false);
      },
      { rootMargin: "200px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage]);

  // Búsqueda por texto sobre lo ya cargado: NIT, razón social y ciudad no son
  // filtrables en Firestore sin un índice de texto, y el universo de entidades
  // es pequeño.
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    // Buscar "900.123.456" tiene que encontrar la entidad aunque el NIT esté
    // guardado sin puntos.
    const digits = term.replace(/\D/g, "");
    return list.filter((e) =>
      [e.nit, e.legalName, e.tradeName, e.city, e.legalRepName]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term)) ||
      (digits && String(e.nit || "").includes(digits))
    );
  }, [list, search]);

  const handleSaved = (saved) => {
    if (saved) dispatch(upsertEntity(saved));
    // El documento guardado vuelve sin los timestamps del servidor, así que se
    // recarga la página para que la lista quede con el orden real.
    loadPage(true);
  };

  if (!allowed) return null;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Entidades</Typography>
        <Tooltip title="Recargar">
          <IconButton onClick={() => loadPage(true)}><RefreshIcon /></IconButton>
        </Tooltip>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
            Nueva entidad
          </Button>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Las organizaciones con las que se firman los convenios, con sus datos y su documentación.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          label="Buscar"
          placeholder="NIT, razón social, ciudad…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <IconButton size="small" onClick={() => setSearch("")}><ClearIcon fontSize="small" /></IconButton>
            ) : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Naturaleza</InputLabel>
          <Select
            label="Naturaleza"
            value={filters.entityType || ""}
            onChange={(e) => dispatch(setEntityFilters({ entityType: e.target.value || null }))}
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.values(ENTITY_TYPE).map((t) => (
              <MenuItem key={t} value={t}>{ENTITY_TYPE_LABELS[t]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={filters.status || ""}
            onChange={(e) => dispatch(setEntityFilters({ status: e.target.value || null }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.values(ENTITY_STATUS).map((s) => (
              <MenuItem key={s} value={s}>{ENTITY_STATUS_LABELS[s]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Atajo a la cola de revisión. Está aparte del selector de estado
            porque es lo único de esta pantalla que hay que MIRAR: las demás
            entidades ya están revisadas y sólo se consultan. */}
        <Button
          size="small"
          variant={filters.status === ENTITY_STATUS.PENDING ? "contained" : "outlined"}
          color="warning"
          startIcon={<PendingActionsIcon />}
          onClick={() =>
            dispatch(setEntityFilters({
              status: filters.status === ENTITY_STATUS.PENDING ? null : ENTITY_STATUS.PENDING,
            }))
          }
          sx={{ whiteSpace: "nowrap" }}
        >
          Pendientes de aprobación
        </Button>
      </Stack>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {LOAD_ERROR_MESSAGE[loadError] || "No fue posible cargar las entidades. Intenta recargar."}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>NIT</TableCell>
              <TableCell>Razón social</TableCell>
              <TableCell>Naturaleza</TableCell>
              <TableCell>Ciudad</TableCell>
              <TableCell align="right">Convenios</TableCell>
              <TableCell>Documentación</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {search || filters.entityType || filters.status
                      ? "Ninguna entidad coincide con la búsqueda."
                      : "Aún no hay entidades registradas."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((entity) => (
                <TableRow
                  key={entity.nit}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/entities/${entity.nit}`)}
                >
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{formatNit(entity.nit)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{entity.legalName}</Typography>
                    {entity.tradeName && (
                      <Typography variant="caption" color="text.secondary">{entity.tradeName}</Typography>
                    )}
                    {/* De dónde salió la entidad. Un registro que llegó del
                        portal y uno que tecleó un gestor son dos grados de
                        confianza distintos, y desde la tabla no se distinguen. */}
                    {entity.registrationSource === ENTITY_REGISTRATION_SOURCE.PUBLIC && (
                      <Chip
                        size="small"
                        variant="outlined"
                        sx={{ ml: 0.5, height: 18, fontSize: "0.65rem" }}
                        label={ENTITY_REGISTRATION_SOURCE_LABELS[ENTITY_REGISTRATION_SOURCE.PUBLIC]}
                      />
                    )}
                  </TableCell>
                  <TableCell>{ENTITY_TYPE_LABELS[entity.entityType] || entity.entityType}</TableCell>
                  <TableCell>{entity.city || "—"}</TableCell>
                  <TableCell align="right">{entity.agreementCount || 0}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={ENTITY_DOC_STATUS_COLOR[entity.documentationStatus] || "default"}
                      label={`${ENTITY_DOC_STATUS_LABELS[entity.documentationStatus] || "—"} · ${entity.completionPercentage || 0}%`}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={ENTITY_STATUS_COLOR[entity.status] || "default"}
                      label={ENTITY_STATUS_LABELS[entity.status] || entity.status}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Centinela de la paginación en cascada: al asomarse pide la página
          siguiente. El margen lo dispara antes de que se vea el final. */}
      <Box ref={sentinelRef} sx={{ height: 1 }} />
      {loadingMore && (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={22} />
        </Stack>
      )}

      <EntityForm
        open={formOpen}
        entity={null}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
    </Container>
  );
}
